import { format } from 'date-fns';
import type { VariationListItem } from '@billda/api';
import type { SitePlot, WorkStatus } from '../../../shared/components/ledger';

/**
 * Adapter: one org-wide variations list row becomes one ledger row. It maps,
 * it never joins other endpoints or invents facts the row DTO lacks — this
 * feed carries no money at all, so the ledger renders with `showValues` off.
 */
export interface VariationLedgerRowModel {
  id: string;
  /** The row's title (decision 15) — never the free-text entry. */
  workTypeName: string;
  /** Demoted off the title line: meta line tail plus hover card. */
  description: string;
  status: WorkStatus;
  /** The allocation reference, absent on variations with no allocation. */
  reference: string | null;
  bookedBy: string;
  /** ISO timestamp. */
  bookedAt: string;
  sitePlot: SitePlot;
  siteId: number;
  plotId: number;
}

export interface VariationWeekGroup {
  /** `${periodYear}-${periodWeekNumber}` — stable across pages and renders. */
  key: string;
  label: string;
  /** The dates the week covers, rendered as the band's secondary label. */
  range?: string;
  rows: VariationLedgerRowModel[];
}

/** Week 0 is the API's "no pay period" marker. */
const UNSCHEDULED_WEEK = 0;

function toRow(item: VariationListItem): VariationLedgerRowModel {
  return {
    id: item.id,
    workTypeName: item.workTypeName,
    description: item.description,
    status: item.status,
    reference: item.uniqueReference,
    bookedBy: item.worker.displayName,
    bookedAt: item.bookedAt,
    sitePlot: { site: item.siteName, plot: item.plotName },
    siteId: item.siteId,
    plotId: item.plotId,
  };
}

function toGroup(items: VariationListItem[]): VariationWeekGroup {
  const [first] = items;
  const key = `${first.periodYear}-${first.periodWeekNumber}`;
  const rows = items.map(toRow);

  if (first.periodWeekNumber === UNSCHEDULED_WEEK) {
    return { key, label: 'Unscheduled', rows };
  }

  const start = new Date(first.periodStart);
  const end = new Date(first.periodEnd);
  return {
    key,
    label: `Week ${first.periodWeekNumber}`,
    range: `${format(start, 'd')}–${format(end, 'd MMM yy')}`,
    rows,
  };
}

/**
 * Groups variations into the pay periods the API stamps them with, newest
 * week first. The period comes from the DTO rather than the booking date —
 * the two disagree for work booked late.
 */
export function toVariationRows(
  items: VariationListItem[]
): VariationWeekGroup[] {
  const byWeek = new Map<string, VariationListItem[]>();
  for (const item of items) {
    const key = `${item.periodYear}-${item.periodWeekNumber}`;
    const existing = byWeek.get(key);
    if (existing) {
      existing.push(item);
    } else {
      byWeek.set(key, [item]);
    }
  }

  return [...byWeek.values()]
    .sort((a, b) => {
      if (a[0].periodYear !== b[0].periodYear) {
        return b[0].periodYear - a[0].periodYear;
      }
      return b[0].periodWeekNumber - a[0].periodWeekNumber;
    })
    .map(toGroup);
}
